import React, {useState, useContext} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {navigationIcMoreVert24px} from '@ey/motif-react-wrapper/assets/icons/index';
import {applyRound} from '@ey/utils';
import {SVGICONS} from '@ey/icons';
import {currentResource, labels, negativeNumberFormattedText} from '../../../../util/utils';
import HasEstimate from '../HasEstimate/HasEstimate';
import AccountName from './AccountName';
import CurrentYearBalance from './CurrentYearBalance';
import Designation from './Designation';
import PriorPeriod from './PriorPeriod';
import XMateriality from './XMateriality';
import EllipseControl from '../../EllipsesControl/EllipsesControl';
import IconButton from '../../BaseComponentWrappers/MotifIconButton';
import DropdownItem from '../../BaseComponentWrappers/MotifDropdownItem';
import Dropdown from '../../BaseComponentWrappers/MotifDropdown';
import {HelixIntegrationContext} from '../../../Main/Ares/Helix/HelixIntegrationModal/index';
import {useUserPreferences} from '../../../../util/customHooks';
import _ from 'lodash';

// ******************* Events inside Start ***************************************i c

//// Handle event when user click on dropdown
export const handleClickDropdownItem = (e) => {
	alert('You clicked a dropdown item. This will be implemented later.');
};

// ******************* Events inside End ***************************************

// ******************* Helper Method Start ***************************************

/// Get in user number format with 2 decimal always
export const getInUserNumberFormat = (value, numberFormat = true, round = 0, suffix = '', pref='') => {
	const userPreferences = pref ? pref : useSelector((store) => store.userPreferences);
	let preferences = JSON.parse(userPreferences?.userPreferencesJson || '{}');
	if (!_.isNil(value)) {
		if (value < 1 && value > 0) {
			// if this is between these 2 then treat it as '1'
			value = 1;
		}

		value = applyRound(value, round); // round it to integer

		if (numberFormat) {
			const negativeNumber = negativeNumberFormattedText(
				userPreferences.numberFormat,
				preferences?.negativenumberformat || 0,
				value === null || value === undefined ? '' : value.toString(),
				suffix
			);
			return negativeNumber;
		}
		return value;
	}

	return '';
};

/// Get in user number format with decimal always
export const UseGetInUserNumberFormatWithDecimals = (value, numberFormat = true) => {
	const userPreferences = useUserPreferences();
	let preferences = JSON.parse(userPreferences.userPreferencesJson);
	if (value !== null) {
		if (numberFormat) {
			return negativeNumberFormattedText(
				userPreferences?.numberFormat,
				preferences?.negativenumberformat || 0,
				value === undefined ? '' : value.toString()
			);
		}
		return value;
	}

	return '';
};

//// Get account type name from resource file
export const getAccountType = (value) => {
	if (value) {
		return currentResource.AccountType.find((account) => account.id === value).accounttypename;
	}

	return labels.notAvailable;
};

/// Get indicator based on risk type associated to account
export const getRiskTypeIndicator = (fraudRisk, significantRisk, inherentRisk) => {
	let newLabel = '';
	if (significantRisk) {
		newLabel = newLabel + labels.significantRiskIndicator + ' / ';
	}
	if (fraudRisk) {
		newLabel = newLabel + labels.fraudRiskIndicator + ' / ';
	}
	if (inherentRisk) {
		newLabel = newLabel + labels.inherentRiskIndicator + ' / ';
	}

	return <EllipseControl content={newLabel.slice(0, -3)} tooltip={newLabel.slice(0, -3)} noOfLines={1} />;
};

// ***************************************  Helper Method End ***************************************

// ****************************************  Start Cell Renderers ********************************************
export const AccountNameCellRender = (params) => <AccountName accountId={params.data.id} />;

export const CurrentYearBalanceCellRender = (params) => <CurrentYearBalance accountId={params.data.id} />;

export const PriorPeriodCellRender = (params) => <PriorPeriod accountId={params.data.id} />;

export const XMaterialityCellRender = (params) => <XMateriality accountId={params.data.id} />;

export const HasEstimatesCellRender = (params) => <HasEstimate accountId={params.data.id} />;

export const DesignationCellRender = (params) => (
	<StyledDesignation key={`${params.data.id}`} accountId={params.data.id} setShowModal={params.colDef.setShowModal} />
);

const StyledDesignation = styled(Designation)`
	display: flex;
	align-items: center;
`;

//// Three dots cell renderer
export const ThreedotCellRender = (params) => {
	const [isOpen, setIsOpen] = useState(false);
	const id = `dropdown-${params.value}`;
	const triggerId = `dropdown-trigger-${params.value}`;
	return (
		<section className="accsummary-cellrenderer">
			<Dropdown
				handleClickOutside={() => setIsOpen(false)}
				open={isOpen}
				id={id}
				ariaLabelledBy={triggerId}
				portalClassName="accsummary-dropdown"
				usePortal
				trigger={
					<IconButton
						id={triggerId}
						type="button"
						onClick={() => setIsOpen(true)}
						ariaControls={id}
						ariaExpanded={isOpen}
						ariaLabel={`dropdown trigger ${params.value}`}
						iconSource={navigationIcMoreVert24px}
					/>
				}
				children={
					<DropdownItem
						onClick={() => {
							setIsOpen(false);
							handleClickDropdownItem();
						}}
						children={labels.delete}
					/>
				}
			/>
		</section>
	);
};
//// AnalyticsCellRender
export const AnalyticsCellRender = (params) => {
	const {accountId, guidances} = params;

	const {setShowModal, setModalProps} = useContext(HelixIntegrationContext);

	const handleOnClick = () => {
		setModalProps({accountId, guidances});
		setShowModal(true);
	};

	return (
		<section className="accsummary-cellrenderer" title={labels.analytics} onClick={handleOnClick}>
			<SVGICONS styleName="medium" name="vector-icon" hoverText={labels.analytics} />
		</section>
	);
};

// ****************************************  End Cell Renderers ********************************************
